// Lógica pura do CampoData: máscara no formato do idioma, leitura do texto digitado e validação do valor ISO.
// Fica fora do componente para ser testada sem DOM (tests/campo-data.test.ts).

export type TipoData = 'date' | 'month' | 'datetime-local' | 'time';
export type IdiomaCampo = 'pt' | 'en' | 'es';

export const ANO_MIN = 1900;
export const ANO_MAX = 2100;

/** en usa mês antes do dia; pt e es usam dia antes do mês. */
const mesPrimeiro = (i: IdiomaCampo) => i === 'en';

/** Quantidade de dígitos que cada tipo exige. */
export const DIGITOS: Record<TipoData, number> = { date: 8, month: 6, time: 4, 'datetime-local': 12 };

const dois = (n: number) => String(n).padStart(2, '0');

function diasDoMes(ano: number, mes: number) {
  return new Date(ano, mes, 0).getDate();
}

const comHora = (s: string) => (s.length > 2 ? `${s.slice(0, 2)}:${s.slice(2)}` : s);

/** Insere barras, dois pontos e espaço conforme o tipo: "15092026" vira "15/09/2026". Ignora tudo que não é dígito. */
export function mascarar(tipo: TipoData, bruto: string): string {
  const d = bruto.replace(/\D/g, '').slice(0, DIGITOS[tipo]);
  if (tipo === 'time') return comHora(d);
  if (tipo === 'month') return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
  const parteData = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('/');
  return tipo === 'datetime-local' && d.length > 8 ? `${parteData} ${comHora(d.slice(8))}` : parteData;
}

/** Valor ISO (como o input nativo guarda) no texto mascarado do idioma; vazio ou fora do padrão vira "". */
export function textoDoValor(tipo: TipoData, iso: string, idioma: IdiomaCampo): string {
  if (!iso) return '';
  const dataDe = (v: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
    if (!m) return '';
    return mesPrimeiro(idioma) ? `${m[2]}/${m[3]}/${m[1]}` : `${m[3]}/${m[2]}/${m[1]}`;
  };
  if (tipo === 'date') return dataDe(iso);
  if (tipo === 'month') {
    const m = /^(\d{4})-(\d{2})/.exec(iso);
    return m ? `${m[2]}/${m[1]}` : '';
  }
  if (tipo === 'time') return /^\d{2}:\d{2}/.test(iso) ? iso.slice(0, 5) : '';
  const [d, h] = iso.split('T');
  const dt = dataDe(d ?? '');
  return dt && h && /^\d{2}:\d{2}/.test(h) ? `${dt} ${h.slice(0, 5)}` : '';
}

function horaValida(h: number, mi: number) {
  return h >= 0 && h <= 23 && mi >= 0 && mi <= 59;
}

/**
 * Texto digitado para valor ISO. Devolve `null` enquanto o texto está incompleto ou inválido
 * (dia inexistente, hora 25, ano fora de 1900 a 2100): o filtro só dispara com data completa e válida.
 */
export function valorDoTexto(tipo: TipoData, texto: string, idioma: IdiomaCampo): string | null {
  const d = texto.replace(/\D/g, '');
  if (d.length !== DIGITOS[tipo]) return null;
  if (tipo === 'time') {
    const [h, mi] = [Number(d.slice(0, 2)), Number(d.slice(2, 4))];
    return horaValida(h, mi) ? `${dois(h)}:${dois(mi)}` : null;
  }
  if (tipo === 'month') {
    const [mes, ano] = [Number(d.slice(0, 2)), Number(d.slice(2, 6))];
    return mes >= 1 && mes <= 12 && ano >= ANO_MIN && ano <= ANO_MAX ? `${ano}-${dois(mes)}` : null;
  }
  const [a, b, ano] = [Number(d.slice(0, 2)), Number(d.slice(2, 4)), Number(d.slice(4, 8))];
  const [dia, mes] = mesPrimeiro(idioma) ? [b, a] : [a, b];
  if (ano < ANO_MIN || ano > ANO_MAX || mes < 1 || mes > 12 || dia < 1 || dia > diasDoMes(ano, mes)) return null;
  const iso = `${ano}-${dois(mes)}-${dois(dia)}`;
  if (tipo === 'date') return iso;
  const [h, mi] = [Number(d.slice(8, 10)), Number(d.slice(10, 12))];
  return horaValida(h, mi) ? `${iso}T${dois(h)}:${dois(mi)}` : null;
}

/** Valor ISO completo e válido (ano de 1900 a 2100): a regra para aplicar um filtro de data. */
export function valorDataValido(tipo: TipoData, iso: string): boolean {
  return valorDoTexto(tipo, textoDoValor(tipo, iso, 'pt'), 'pt') !== null;
}

/** `min` e `max` do input (ISO) contra o valor; a comparação em texto preserva a ordem do ISO. */
export function dentroDoIntervalo(iso: string, min?: string | number, max?: string | number) {
  if (min !== undefined && min !== '' && iso < String(min)) return false;
  if (max !== undefined && max !== '' && iso > String(max)) return false;
  return true;
}

export type EstadoTexto = 'vazio' | 'incompleto' | 'invalido' | 'valido';

/**
 * Situação do texto digitado: `incompleto` ainda pode ser terminado; `invalido` é completo mas não existe
 * (31/02/2026, hora 25) ou está fora de min e max. Só o `valido` pode virar valor.
 */
export function estadoDoTexto(tipo: TipoData, texto: string, idioma: IdiomaCampo, min?: string | number, max?: string | number): EstadoTexto {
  if (!texto) return 'vazio';
  if (texto.replace(/\D/g, '').length !== DIGITOS[tipo]) return 'incompleto';
  const iso = valorDoTexto(tipo, texto, idioma);
  return iso !== null && dentroDoIntervalo(iso, min, max) ? 'valido' : 'invalido';
}
